import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, map, Observable, shareReplay } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
   Country,
   Hit,
   MarketCollectionResponse,
} from 'src/app/models/market-collection/market-collection.model';
import { TYPESENSE_COLLECTIONS } from '../constants/typesense-collections';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { FilterByCountryPopupComponent } from '../components/filter-by-country-popup/filter-by-country-popup.component';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
   providedIn: 'root',
})
export class CountryService {
   private countries$!: Observable<Country[]>;

   private selectedCountrySubject: BehaviorSubject<Country>;
   public selectedCountry$: Observable<Country>;

   filterByCountryPopupRef!: NgbModalRef;
   private filterCountrySubject!: BehaviorSubject<Country>;
   public filterCountry$!: Observable<Country>;

   constructor(
      private http: HttpClient, 
      private modalService: NgbModal, 
      @Inject(PLATFORM_ID) private platformId: Object
   ) {
      const defaultCountry: Country = {
         country: 'United States',
         countryCode: 'US',
         detailedProfileAvailable: true,
         etfEnabled: true,
         etfExchanges: ['NYSE ARCA', 'NASDAQ'],
         etfsCovered: true,
         id: 'US',
         sortOrder: 1,
         stockEnabled: true,
         stockExchanges: ['NYSE', 'NASDAQ'],
         stocksCovered: true,
      };

      this.selectedCountrySubject = new BehaviorSubject<Country>(
         defaultCountry
      );
      this.selectedCountry$ = this.selectedCountrySubject.asObservable();

      this.filterCountrySubject = new BehaviorSubject<Country>(defaultCountry);
      this.filterCountry$ = this.filterCountrySubject.asObservable();

      if (isPlatformBrowser(this.platformId)) {
         const storedCountryCode = localStorage.getItem('dataFilterCountry');
         if (storedCountryCode) {
            this.setSelectedCountryByCode(storedCountryCode);
         }
      }
   }

   getCountries(): Observable<Country[]> {
      this.countries$ =
         this.countries$ ?? this.fetchCountries().pipe(shareReplay());
      return this.countries$;
   }

   private fetchCountries(): Observable<Country[]> {
      const queryParams = new HttpParams().set('per_page', '250').set('q', '*');

      return this.http
         .get<MarketCollectionResponse>(
            `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.MARKET_COLLECTION}/documents/search`,
            { params: queryParams }
         )
         .pipe(
            map(
               (response) =>
                  response.hits
                     .map((hits: Hit) => hits.document)
                     .filter(
                        (country: Country) => country.countryCode !== 'IL'
                     ) as Country[]
            )
         );
   }

   setSelectedCountry(country: Country): void {
      if (isPlatformBrowser(this.platformId)) {
         localStorage.setItem('dataFilterCountry', country.countryCode);
      }
      this.selectedCountrySubject.next(country);
   }

   getSelectedCountry(): Country {
      return this.selectedCountrySubject.value;
   }

   openFilterByCountriesPopup() {
      if (!isPlatformBrowser(this.platformId)) return;
      
      this.filterByCountryPopupRef = this.modalService.open(
         FilterByCountryPopupComponent,
         {
            centered: true,
            scrollable: true,
            backdropClass: 'global-modal-backdrop',
            windowClass: 'global-modal',
            size: 'lg',
         }
      );
   }

   closeFilterByCountriePopup() {
      if (this.filterByCountryPopupRef) {
         this.filterByCountryPopupRef.close();
      }
   }

   setFilterCountry(country: Country): void {
      // sessionStorage.setItem('selectedFilterCountry', JSON.stringify(country));
      return this.filterCountrySubject.next(country);
   }

   getFilterCountry(): Country {
      return this.filterCountrySubject.value;
   }

   // Add this method to your CountryService
   setSelectedCountryByCode(countryCode: string): void {
      if (!isPlatformBrowser(this.platformId)) return;

      this.getCountries().subscribe((countries) => {
         const country = countries.find((c) => c.countryCode === countryCode);
         if (country) {
            this.setSelectedCountry(country);
         }
      });
   }

   getTradingAllowedCountryCodes(): string[] {
      return [
         'IN', // India
         'US', // United States
         'GB', // United Kingdom
         'AE', // United Arab Emirates
         'FR', // France
         'SA', // Saudi Arabia
         'QA', // Qatar
         'DE', // Germany
         'AU', // Australia
         'MY', // Malaysia
         'SG', // Singapore
         'EG', // Egypt
         'KW', // Kuwait
         'NL', // Netherlands
         'JO', // Jordan
         'AX', // Åland Islands
         'AS', // American Samoa
         'AD', // Andorra
         'AI', // Anguilla
         'AG', // Antigua and Barbuda
         'AR', // Argentina
         'AM', // Armenia
         'AW', // Aruba
         'AT', // Austria
         'BS', // Bahamas
         'BH', // Bahrain
         'BB', // Barbados
         'BE', // Belgium
         'BJ', // Benin
         'BM', // Bermuda
         'BT', // Bhutan
         'BQ', // Bonaire, Sint Eustatius and Saba
         'BW', // Botswana
         'IO', // British Indian Ocean Territory
         'BN', // Brunei Darussalam
         'BG', // Bulgaria
         'CV', // Cabo Verde
         'KY', // Cayman Islands
         'CL', // Chile
         'CX', // Christmas Island
         'CC', // Cocos (Keeling) Islands
         'CO', // Colombia
         'CK', // Cook Islands
         'CR', // Costa Rica
         'CW', // Curaçao
         'CY', // Cyprus
         'CZ', // Czechia
         'DK', // Denmark
         'DM', // Dominica
         'DO', // Dominican Republic
         'EE', // Estonia
         'FK', // Falkland Islands
         'FO', // Faroe Islands
         'FJ', // Fiji
         'FI', // Finland
         'GF', // French Guiana
         'PF', // French Polynesia
         'GM', // Gambia
         'GH', // Ghana
         'GI', // Gibraltar
         'GR', // Greece
         'GL', // Greenland
         'GD', // Grenada
         'GP', // Guadeloupe
         'GU', // Guam
         'GG', // Guernsey
         'VA', // Holy See
         'HK', // Hong Kong
         'HU', // Hungary
         'IS', // Iceland
         'IE', // Ireland
         'IM', // Isle of Man
         'IL', // Israel
         'IT', // Italy
         'JP', // Japan
         'JE', // Jersey
         'KZ', // Kazakhstan
         'KI', // Kiribati
         'KR', // South Korea
         'LV', // Latvia
         'LS', // Lesotho
         'LI', // Liechtenstein
         'LT', // Lithuania
         'LU', // Luxembourg
         'MO', // Macao
         'MW', // Malawi
         'MV', // Maldives
         'MT', // Malta
         'MH', // Marshall Islands
         'MQ', // Martinique
         'MU', // Mauritius
         'YT', // Mayotte
         'FM', // Micronesia
         'MD', // Moldova
         'MC', // Monaco
         'MN', // Mongolia
         'ME', // Montenegro
         'MS', // Montserrat
         'MA', // Morocco
         'NA', // Namibia
         'NR', // Nauru
         'NC', // New Caledonia
         'NZ', // New Zealand
         'NU', // Niue
         'NF', // Norfolk Island
         'MP', // Northern Mariana Islands
         'NO', // Norway
         'OM', // Oman
         'PW', // Palau
         'PN', // Pitcairn
         'PL', // Poland
         'PT', // Portugal
         'PR', // Puerto Rico
         'RE', // Réunion
         'RO', // Romania
         'RW', // Rwanda
         'BL', // Saint Barthélemy
         'KN', // Saint Kitts and Nevis
         'LC', // Saint Lucia
         'SX', // Sint Maarten
         'MF', // Saint Martin (French part)
         'PM', // Saint Pierre and Miquelon
         'VC', // Saint Vincent and the Grenadines
         'WS', // Samoa
         'SM', // San Marino
         'ST', // Sao Tome and Principe
         'SC', // Seychelles
         'SK', // Slovakia
         'SI', // Slovenia
         'SB', // Solomon Islands
         'ES', // Spain
         'LK', // Sri Lanka
         'SJ', // Svalbard and Jan Mayen
         'SE', // Sweden
         'CH', // Switzerland
         'TW', // Taiwan
         'TL', // Timor-Leste
         'TK', // Tokelau
         'TO', // Tonga
         'TC', // Turks and Caicos Islands
         'TV', // Tuvalu
         'UM', // United States Minor Outlying Islands
         'UY', // Uruguay
         // 'UZ', // Uzbekistan
         'VG', // Virgin Islands (British)
         'VI', // Virgin Islands (U.S.)
         'WF', // Wallis and Futuna
         'EH', // Western Sahara
         'ZM', // Zambia
      ]
   }
}

// import { Injectable } from '@angular/core';
// import { BehaviorSubject } from 'rxjs';

// @Injectable({
//    providedIn: 'root'
// })

// export class CountryService {
//    private selectedCountrySubject = new BehaviorSubject<string>('US');
//    selectedCountry$ = this.selectedCountrySubject.asObservable();

//    constructor() {}

//    setSelectedCountry(country: string): void {
//       this.selectedCountrySubject.next(country);
//    }
// }
